import React, { useEffect, useState } from 'react';
import { FiX } from 'react-icons/fi';
import api from '../services/api';
import { localInputToISO, toLocalInput } from '../utils/techTask';

const TechPipelineModal = ({ task, onClose, onSuccess }) => {
  const isEdit = Boolean(task);
  const [assignees, setAssignees] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    title: task?.title || '',
    description: task?.description || '',
    task_type: task?.task_type || 'feature',
    priority: task?.priority || 'medium',
    client_name: task?.client_name || '',
    assigned_to: task?.assigned_to ? String(task.assigned_to) : '',
    due_date: toLocalInput(task?.due_date),
  });

  useEffect(() => {
    api.get('/tech-tasks/assignees').then((r) => setAssignees(r.data.users || [])).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    const payload = {
      title: form.title,
      description: form.description,
      task_type: form.task_type,
      priority: form.priority,
      client_name: form.client_name,
      assigned_to: form.assigned_to ? parseInt(form.assigned_to, 10) : null,
      due_date: localInputToISO(form.due_date),
      status: 'pipeline',
    };
    try {
      if (isEdit) await api.put(`/tech-tasks/${task.id}`, payload);
      else await api.post('/tech-tasks', payload);
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save pipeline task');
    } finally {
      setSaving(false);
    }
  };

  const field = 'w-full px-3 py-2.5 rounded-lg border border-gray-300 text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500';

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div
        className="bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-md max-h-[92vh] overflow-y-auto p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">{isEdit ? 'Edit pipeline task' : 'Add pipeline task'}</h2>
          <button type="button" onClick={onClose} className="p-2 text-gray-500">
            <FiX size={22} />
          </button>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Future work for the pipeline. Optionally assign a teammate — anyone can open unassigned tasks.
        </p>
        {error && <p className="mb-3 text-sm text-red-600">{error}</p>}
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-xs font-medium text-gray-600">Title *</label>
            <input className={field} required value={form.title} onChange={(e) => set('title', e.target.value)} />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600">Description</label>
            <textarea className={field} rows={3} value={form.description} onChange={(e) => set('description', e.target.value)} />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600">Client</label>
            <input className={field} value={form.client_name} onChange={(e) => set('client_name', e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-medium text-gray-600">Type</label>
              <select className={field} value={form.task_type} onChange={(e) => set('task_type', e.target.value)}>
                <option value="feature">Feature</option>
                <option value="bug">Bug</option>
                <option value="incident">Incident</option>
                <option value="maintenance">Maintenance</option>
                <option value="chore">Chore</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">Priority</label>
              <select className={field} value={form.priority} onChange={(e) => set('priority', e.target.value)}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600">Assign to (optional)</label>
            <select className={field} value={form.assigned_to} onChange={(e) => set('assigned_to', e.target.value)}>
              <option value="">Unassigned</option>
              {assignees.map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600">Planned date (IST)</label>
            <input type="datetime-local" className={field} value={form.due_date} onChange={(e) => set('due_date', e.target.value)} />
          </div>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-3 rounded-xl border border-gray-300 text-gray-700 font-medium">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="flex-1 py-3 rounded-xl bg-teal-600 text-white font-semibold disabled:opacity-50">
              {saving ? 'Saving…' : isEdit ? 'Save' : 'Add to pipeline'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TechPipelineModal;
